import { useState, useEffect } from 'react';
import logo from './app-icon-svg.svg';
import './App.css';

function App() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const styles = {
    // Hero Section
    hero: {
      background: 'linear-gradient(135deg, #0D224C 0%, #0D224C 100%)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      color: 'white',
      textAlign: 'center'
    },

    heroContent: {
      maxWidth: '800px',
      padding: '2rem',
      zIndex: 1,
      position: 'relative',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      transition: 'all 1s ease-out'
    },

    logo: {
      width: '120px',
      height: '120px',
      margin: '0 auto 2rem',
      background: 'white',
      borderRadius: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '3rem',
      fontWeight: 'bold',
      color: '#0D224C',
      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
      animation: 'float 6s ease-in-out infinite'
    },

    heroTitle: {
      fontSize: 'clamp(2.5rem, 5vw, 4rem)',
      fontWeight: '700',
      marginBottom: '1rem',
      background: 'linear-gradient(45deg, #fff, #f0f0f0)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },

    heroSubtitle: {
      fontSize: '1.4rem',
      fontWeight: '300',
      marginBottom: '2rem',
      opacity: 0.9
    },

    heroDescription: {
      fontSize: '1.1rem',
      marginBottom: '3rem',
      opacity: 0.8,
      lineHeight: 1.7
    },

    downloadButtons: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap',
      marginBottom: '2rem'
    },

    downloadBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '1rem 2rem',
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      textDecoration: 'none',
      borderRadius: '15px',
      fontWeight: '600',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      transition: 'all 0.3s ease',
      fontSize: '1.1rem'
    },

    downloadBtnPrimary: {
      background: '#FFCC00',
      color: '#181C20',
      border: 'none'
    },

    comingSoon: {
      color: '#FFCC00',
      fontWeight: '500',
      opacity: 0.9
    },

    // How It Works Section
    howItWorks: {
      padding: '5rem 2rem',
      background: 'linear-gradient(to bottom, #f8fafc, #fff)'
    },

    container: {
      maxWidth: '1200px',
      margin: '0 auto'
    },

    sectionTitle: {
      textAlign: 'center',
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#1a202c',
      marginBottom: '1rem'
    },

    sectionSubtitle: {
      textAlign: 'center',
      fontSize: '1.2rem',
      color: '#718096',
      marginBottom: '4rem',
      maxWidth: '600px',
      marginLeft: 'auto',
      marginRight: 'auto'
    },

    steps: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '3rem',
      marginTop: '3rem'
    },

    step: {
      textAlign: 'center',
      padding: '2rem',
      background: 'white',
      borderRadius: '20px',
      boxShadow: '0 4px 25px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden'
    },

    stepIcon: {
      width: '80px',
      height: '80px',
      margin: '0 auto 1.5rem',
      background: 'linear-gradient(135deg, #0D224C, #0D224C)',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '2rem',
      color: 'white'
    },

    stepTitle: {
      fontSize: '1.4rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#1a202c'
    },

    stepDescription: {
      color: '#718096',
      lineHeight: 1.6
    },

    // Features Section
    features: {
      padding: '5rem 2rem',
      background: '#1a202c',
      color: 'white'
    },

    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '2rem',
      marginTop: '3rem'
    },

    feature: {
      padding: '2rem',
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '15px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease'
    },

    featureIcon: {
      fontSize: '2.5rem',
      marginBottom: '1rem',
      display: 'block'
    },

    featureTitle: {
      fontSize: '1.2rem',
      marginBottom: '0.5rem',
      color: '#FFCC00'
    },

    // About Section
    about: {
      padding: '5rem 2rem',
      background: 'linear-gradient(135deg, #FFCC00, #f0b90b)',
      color: '#1a202c'
    },

    aboutContent: {
      maxWidth: '800px',
      margin: '0 auto',
      textAlign: 'center'
    },

    aboutTitle: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '2rem'
    },

    aboutText: {
      fontSize: '1.2rem',
      lineHeight: 1.8,
      marginBottom: '2rem'
    },

    // CTA Section
    cta: {
      padding: '5rem 2rem',
      background: 'linear-gradient(135deg, #0D224C 0%, #0D224C 100%)',
      color: 'white',
      textAlign: 'center'
    },

    ctaTitle: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '1rem'
    },

    ctaText: {
      fontSize: '1.2rem',
      marginBottom: '2rem',
      opacity: 0.9
    },

    // Footer
    footer: {
      background: '#0d1117',
      color: 'white',
      padding: '3rem 2rem 2rem'
    },

    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '2rem',
      marginBottom: '2rem'
    },

    footerSectionTitle: {
      color: '#FFCC00',
      marginBottom: '1rem'
    },

    footerLink: {
      color: '#718096',
      textDecoration: 'none',
      display: 'block',
      marginBottom: '0.5rem',
      transition: 'color 0.3s ease'
    },

    footerBottom: {
      textAlign: 'center',
      paddingTop: '2rem',
      borderTop: '1px solid #30363d',
      color: '#718096'
    }
  };

  return (
    <div className="App">
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-10px) rotate(1deg); }
          75% { transform: translateY(-5px) rotate(-1deg); }
        }

        .step:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 40px rgba(0,0,0,0.12);
        }

        .step::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #0D224C, #0D224C);
        }

        .feature:hover {
          background: rgba(255, 255, 255, 0.08);
          transform: translateY(-3px);
        }

        .download-btn:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.1);
        }

        .download-btn-primary:hover {
          background: #e6b800;
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(255, 204, 0, 0.3);
        }

        .footer-link:hover {
          color: #FFCC00;
        }

        @media (max-width: 768px) {
          .download-buttons {
            flex-direction: column;
            align-items: center;
          }
          
          .steps {
            grid-template-columns: 1fr;
          }
          
          .features-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <div style={styles.logo}>
            <img
              src={logo}
              width={250}
              height={250}
              alt="App Logo"
            />
          </div>
          <h1 style={styles.heroTitle}>Welcome to Cirkel</h1>
          <p style={styles.heroSubtitle}>Connecting Needs...</p>
          <p style={styles.heroDescription}>
            Cirkel is a revolutionary way to find exactly what you need. Instead of endless scrolling through
            products, simply request what you're looking for and let people with the perfect match come to you.
            It's commerce, reimagined.
          </p>

          <div style={styles.downloadButtons}>
            <a
              href="https://apps.apple.com/gh/app/cirkel/id6743814011"
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...styles.downloadBtn, ...styles.downloadBtnPrimary }}
              className="download-btn-primary"
            >
              <span>📱</span>
              Download on App Store
            </a>
          </div>

          <p style={styles.comingSoon}>🚀 Coming soon to Google Play Store</p>
        </div>
      </section>

      {/* How It Works Section */}
      <section style={styles.howItWorks}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>How Cirkel Works</h2>
          <p style={styles.sectionSubtitle}>Three simple steps to get exactly what you need</p>

          <div style={styles.steps}>
            <div style={styles.step} className="step">
              <div style={styles.stepIcon}>🎯</div>
              <h3 style={styles.stepTitle}>1. Make Your Request</h3>
              <p style={styles.stepDescription}>
                Describe exactly what you're looking for with details, photos, and your budget.
                Be as specific as possible to get the best matches.
              </p>
            </div>

            <div style={styles.step} className="step">
              <div style={styles.stepIcon}>🤝</div>
              <h3 style={styles.stepTitle}>2. Receive Perfect Offers</h3>
              <p style={styles.stepDescription}>
                People who have what you need will respond with offers. Compare options,
                chat with providers, and ask questions.
              </p>
            </div>

            <div style={styles.step} className="step">
              <div style={styles.stepIcon}>✅</div>
              <h3 style={styles.stepTitle}>3. Complete Your Circle</h3>
              <p style={styles.stepDescription}>
                Choose the best offer, arrange the details, and complete your transaction.
                Rate your experience to help the community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={styles.features}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Why Choose Cirkel?</h2>
          <p style={styles.sectionSubtitle}>Experience a better way to find exactly what you need</p>

          <div style={styles.featuresGrid}>
            {[
              { icon: '🔍', title: 'Find the Unfindable', desc: 'Locate rare, discontinued, or hard-to-find items that traditional search can\'t uncover.' },
              { icon: '💯', title: 'No More Settling', desc: 'Get exactly what you want, not just what\'s available. No more "close enough" purchases.' },
              { icon: '🌐', title: 'Community Powered', desc: 'Tap into a network of people with diverse access, knowledge, and resources.' },
              { icon: '⚡', title: 'Save Time', desc: 'Post once instead of searching endlessly. Let the right solutions come to you.' },
              { icon: '🛡️', title: 'Safe & Secure', desc: 'Built-in messaging, user verification, and community trust systems keep you protected.' },
              { icon: '🎨', title: 'Custom Solutions', desc: 'Find people who can create, modify, or source exactly what you have in mind.' }
            ].map((feature, index) => (
              <div key={index} style={styles.feature} className="feature">
                <span style={styles.featureIcon}>{feature.icon}</span>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section style={styles.about}>
        <div style={styles.container}>
          <div style={styles.aboutContent}>
            <h2 style={styles.aboutTitle}>Our Story</h2>
            <p style={styles.aboutText}>
              Inspired by the vibrant trading culture at Ghana's iconic Circle—where people gather to buy,
              sell, and find exactly what they need—Cirkel brings this community-driven commerce to the digital world.
            </p>
            <p style={styles.aboutText}>
              We believe that finding what you need shouldn't be a matter of luck or endless searching.
              By putting your specific needs first, Cirkel creates meaningful connections between people
              who want something and those who can provide it.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section style={styles.cta}>
        <div style={styles.container}>
          <h2 style={styles.ctaTitle}>Ready to Find Exactly What You Need?</h2>
          <p style={styles.ctaText}>Join the Cirkel community and experience request-based commerce today.</p>

          <div style={styles.downloadButtons}>
            <a
              href="https://apps.apple.com/gh/app/cirkel/id6743814011"
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...styles.downloadBtn, ...styles.downloadBtnPrimary }}
              className="download-btn-primary"
            >
              📱 Get Started on iOS
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.container}>
          <div style={styles.footerContent}>
            <div>
              <h3 style={styles.footerSectionTitle}>Cirkel</h3>
              <p>Connecting needs, completing circles.</p>
            </div>

            <div>
              <h3 style={styles.footerSectionTitle}>Product</h3>
              <a href="#how-it-works" style={styles.footerLink} className="footer-link">How it Works</a>
              <a href="#features" style={styles.footerLink} className="footer-link">Features</a>
              <a href="#download" style={styles.footerLink} className="footer-link">Download</a>
            </div>

            <div>
              <h3 style={styles.footerSectionTitle}>Support</h3>
              <a href="#help" style={styles.footerLink} className="footer-link">Help Center</a>
              <a href="#contact" style={styles.footerLink} className="footer-link">Contact Us</a>
              <a href="https://anoobadevs.github.io/cirkel/cirkel-privacy-html.html" style={styles.footerLink} className="footer-link">Privacy Policy</a>
              <a href="https://anoobadevs.github.io/cirkel/cirkel-terms-html.html" style={styles.footerLink} className="footer-link">Terms of Service</a>
            </div>

            <div>
              <h3 style={styles.footerSectionTitle}>Connect</h3>
              <a href="https://x.com/Cirkelgh" style={styles.footerLink} className="footer-link">Twitter</a>
              <a href="https://linkedin.com/company/cirkelgh" style={styles.footerLink} className="footer-link">LinkedIn</a>
              <a href="https://www.tiktok.com/@cirkelgh" style={styles.footerLink} className="footer-link">TikTok</a>
              {/* <a href="#instagram" style={styles.footerLink} className="footer-link">Instagram</a> */}
            </div>
          </div>

          <div style={styles.footerBottom}>
            <p>&copy; {new Date().getFullYear()} Cirkel. All rights reserved. | Connecting needs across Ghana and beyond.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;